import * as Sentry from '@sentry/react';
import axios, { type AxiosError } from 'axios';
import {
  isSentryCaptured,
  isSentryInitialized,
  markSentryCaptured,
  normalizeError,
} from '@payler/sentry/merchant-cabinet';

function getEndpoint(url?: string): string | undefined {
  if (!url) return undefined;
  return url.split('?')[0]?.split('#')[0];
}

function summarizePayload(value: unknown):
  | { type: 'undefined' }
  | { type: 'null' }
  | { type: 'string'; length: number }
  | { type: 'number' | 'boolean' | 'bigint' | 'symbol' }
  | { type: 'function' }
  | { type: 'array'; length: number }
  | { type: 'object'; keys: string[]; truncatedKeys: boolean } {
  if (value === undefined) return { type: 'undefined' };
  if (value === null) return { type: 'null' };
  if (typeof value === 'string') return { type: 'string', length: value.length };
  if (
    typeof value === 'number' ||
    typeof value === 'boolean' ||
    typeof value === 'bigint' ||
    typeof value === 'symbol'
  ) {
    const t = typeof value as 'number' | 'boolean' | 'bigint' | 'symbol';
    return { type: t };
  }
  if (typeof value === 'function') return { type: 'function' };
  if (Array.isArray(value)) return { type: 'array', length: value.length };
  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const keys = Object.keys(obj);
    const maxKeys = 50;
    return {
      type: 'object',
      keys: keys.slice(0, maxKeys),
      truncatedKeys: keys.length > maxKeys,
    };
  }
  // fallback
  return { type: 'undefined' };
}

export function captureApiServerError(error: unknown): void {
  if (!axios.isAxiosError(error)) return;

  const marked = error as AxiosError;
  if (isSentryCaptured(marked)) return;

  const status = marked.response?.status;
  if (typeof status !== 'number' || status < 500) return;
  if (!isSentryInitialized()) return;

  const method = (marked.config?.method ?? 'get').toUpperCase();
  const endpoint = getEndpoint(marked.config?.url);

  try {
    Sentry.withScope((scope) => {
      scope.setLevel('error');
      scope.setTag('error.kind', 'api_server_error');
      scope.setTag('http.method', method);
      scope.setTag('http.status_code', String(status));
      if (endpoint) scope.setTag('api.endpoint', endpoint);

      scope.setContext('api', {
        method,
        endpoint,
        status,
        // Intentionally avoid sending request/response payload values to Sentry (PII risk).
        params: summarizePayload((marked.config as any)?.params),
        requestData: summarizePayload((marked.config as any)?.data),
        responseData: summarizePayload(marked.response?.data),
      });

      // Avoid capturing raw AxiosError (can include sensitive headers).
      Sentry.captureException(
        normalizeError(
          endpoint
            ? `API server error ${method} ${endpoint} (${status})`
            : `API server error ${method} (${status})`,
        ),
      );
    });
  } finally {
    markSentryCaptured(marked, 'api');
  }
}
